import React from "react"
import { NavLink, useNavigate } from "react-router-dom"
import { createUseStyles, useTheme } from "react-jss"
import { GlobalToken } from "antd/es/theme/interface"
import { Collapse } from "antd"
import { ListProps } from "./Header"
import LeftArrowButton from "../../components/LeftArrowButton"

const { Panel } = Collapse

const useStyles = createUseStyles<string, { active: boolean }, GlobalToken>(theme => ({
    menuDrawer: {
        position: "fixed",
        inset: 0,
        zIndex: 999,
        pointerEvents: (props) => props.active ? "auto" : "none"
    },
    active: {
        padding: "20px 16px",
        color: theme.colorPrimary,
        fontWeight: 600,
        background: "rgba(20, 88, 57, 0.1)",
    },
    bg: {
        position: "absolute",
        inset: 0,
        zIndex: 998,
        background: "rgba(0, 0, 0, 0.25)",
        backdropFilter: "blur(4px)",
        opacity: (props) => props.active ? 1 : 0,
        transition: "opacity 0.2s ease"
    },
    menuListBlock: {
        position: "absolute",
        left: (props) => props.active ? 0 : "-100%",
        top: 0,
        bottom: 0,
        display: "flex",
        flexDirection: "column",
        background: "#fff",
        minWidth: "280px",
        maxWidth: "90vw",
        padding: "20px 40px",
        boxShadow: "0 4px 24px rgba(0, 0, 0, 0.12)",
        borderRadius: "0 16px 16px 0",
        zIndex: 999,
        transition: "left 0.25s ease-in-out",
    },
    listItem: {
        display: "block",
        padding: "16px 0",
        width: "100%",
        fontWeight: 500,
        fontSize: theme.fontSize,
        lineHeight: 1.5,
        cursor: "pointer",
        color: theme.colorText,
        borderBottom: "1px solid rgba(181, 181, 181, 0.3)",
        transition: "all 0.2s ease",
        "&:hover": {
            color: theme.colorPrimary,
            transform: "translateX(8px)"
        }
    },
    subItem: {
        display: "block",
        padding: "16px 15px",
        width: "100%",
        fontWeight: 500,
        fontSize: theme.fontSize,
        lineHeight: 1.5,
        cursor: "pointer",
        color: theme.colorText,
        transition: "all 0.2s ease",
        "&:hover": {
            color: theme.colorPrimary,
            transform: "translateX(8px)"
        },
        "&:not(:last-child)": {
            borderBottom: "1px solid rgba(181, 181, 181, 0.3)"
        }
    },
    menuLogo: {
        height: "40px",
        width: "160px",
        cursor: "pointer",
        "& img": {
            width: "100%",
            height: "100%",
            objectFit: "contain",
            verticalAlign: "middle"
        }
    },
    wrapper: {
        display: "flex",
        alignItems: "center",
        justifyContent: "space-between",
        gap: "40px",
        borderBottom: "1px solid rgba(181, 181, 181, 0.3)",
        padding: "20px 0",
        marginBottom: "16px"
    },
    closeNavBtn: {
        width: "64px",
        height: "32px",
        background: "#fff",
        border: `1px solid ${theme.colorPrimary}`,
        cursor: "pointer",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        transition: "all 0.2s ease",
        "&:hover": {
            background: theme.colorPrimary,
            color: "#fff"
        }
    },
    collapseButton: {
        borderRadius: "none",
        boxShadow: "none",
        width: "100%",
        cursor: "pointer",
        borderBottom: "1px solid rgba(181, 181, 181, 0.3)",
        ".ant-collapse-header": {
            padding: "16px 0 !important",
            transition: "color 0.2s ease",
            "&:hover": {
                color: theme.colorPrimary
            }
        }
    }
}))

interface DrawerMenuProps {
    active: boolean
    onClose: () => void
    list?: ListProps[]
}

const HeaderDrawer: React.FC<DrawerMenuProps> = ({ active, onClose, list }) => {
    const theme = useTheme<GlobalToken>()
    const classes = useStyles({ theme, active })
    const navigate = useNavigate()

    const onClickHandler = () => navigate("/")

    const renderLinkItem = (item: ListProps, index: number) => (
        <NavLink
            key={item.link || `${item.title}-${index}`}
            to={item.link!}
            onClick={onClose}
            className={({ isActive }) => `${classes.subItem} ${isActive ? classes.active : ''}`}
        >
            {item.title}
        </NavLink>
    )

    return (
        <div className={classes.menuDrawer}>
            <div onClick={onClose} className={classes.bg} />
            <div className={classes.menuListBlock}>
                <div className={classes.wrapper}>
                    <div className={classes.menuLogo} onClick={onClickHandler}>
                        <img src="/images/logo.png" alt="Bellissimo Pizza logo" loading="lazy" />
                    </div>
                    <LeftArrowButton onClick={onClose} />
                </div>

                {list?.filter(item => !item.hidden).map((item, index) => {
                    if (item.children?.length) {
                        return (
                            <Collapse key={`${item.title}-${index}`} ghost className={classes.collapseButton}>
                                <Panel header={item.title} key={`${item.title}-panel`}>
                                    {item.children
                                        .filter(child => !child.hidden)
                                        .map((child, idx) => renderLinkItem(child, idx))}
                                </Panel>
                            </Collapse>
                        )
                    }
                    return item.link ? renderLinkItem(item, index) : null
                })}
            </div>
        </div>
    )
}

export default HeaderDrawer
